import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown, Clock, FileCheck2, Recycle, MapPin } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, IMAGES } from "../../lib/constants";
import { trackPhoneClick } from "../../lib/gtag";

const LINES = [
  { text: "Gyors és Tiszta", accent: false },
  { text: "Lomtalanítás", accent: true },
  { text: "– Megszabadítjuk a felesleges hulladéktól!", accent: false, small: true },
];

const BADGES = [
  { icon: Clock, label: "Munkavégzés 24-48 órán belül", testid: "badge-fast" },
  { icon: FileCheck2, label: "Számlaképes szolgáltatás", testid: "badge-invoice" },
  { icon: Recycle, label: "Környezetbarát hulladékkezelés", testid: "badge-eco" },
];

export default function Hero({ scrollTo }) {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      id="hero"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-32"
    >
      <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-green-100/60 blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 bg-white border border-[#E2E8F0] rounded-full px-4 py-2 mb-8 shadow-sm"
            data-testid="hero-kicker"
          >
            <MapPin className="w-4 h-4 text-[#16A34A]" />
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#475569]">
              Hétvégén is dolgozunk
            </span>
          </motion.div>

          <h1 className="font-display font-black tracking-tighter text-[#1E293B] text-4xl sm:text-5xl lg:text-7xl leading-[1.02]" data-testid="hero-headline">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.accent ? "text-[#16A34A]" : ""} ${line.small ? "text-2xl sm:text-3xl lg:text-4xl font-bold text-[#475569] mt-3" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
            className="mt-6 text-base md:text-lg text-[#64748B] leading-relaxed max-w-xl"
            data-testid="hero-subheadline"
          >
            Lomtalanítás, pinceürítés, lakáskiürítés és építési törmelék elszállítása
            hétvégén is, rejtett költségek nélkül.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-8 flex flex-col sm:flex-row gap-4"
          >
            <button
              data-testid="hero-quote-cta"
              onClick={() => scrollTo("#kapcsolat")}
              className="group bg-[#1E293B] hover:bg-[#0F172A] text-white font-bold py-4 px-8 rounded-full text-base flex items-center justify-center gap-3 shadow-xl transition-colors"
            >
              Díjmentes Ajánlatkérés
              <ArrowDown className="w-4 h-4 transition-transform group-hover:translate-y-1" />
            </button>
            <a
              data-testid="hero-phone-cta"
              href={PHONE_TEL}
              onClick={trackPhoneClick}
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-8 rounded-full text-base flex items-center justify-center gap-3 shadow-lg shadow-green-600/30 transition-colors"
            >
              <Phone className="w-5 h-5" />
              {PHONE_DISPLAY}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex flex-wrap gap-3"
            data-testid="hero-trust-badges"
          >
            {BADGES.map(({ icon: Icon, label, testid }) => (
              <div
                key={testid}
                data-testid={testid}
                className="flex items-center gap-2 bg-white border border-[#E2E8F0] rounded-full px-4 py-2.5 shadow-sm"
              >
                <Icon className="w-4 h-4 text-[#16A34A]" />
                <span className="text-xs sm:text-sm font-semibold text-[#1E293B]">{label}</span>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.div style={{ y: imageY }} className="rounded-[2rem] overflow-hidden shadow-2xl shadow-slate-900/15 border-8 border-white">
              <img
                src={IMAGES.hero}
                alt="Lomtalanító csapat munka közben"
                className="w-full h-[380px] sm:h-[480px] object-cover"
                data-testid="hero-image"
              />
            </motion.div>
            <motion.div
              style={{ y: cardY }}
              className="absolute -bottom-6 -left-4 sm:-left-8 bg-white rounded-2xl shadow-xl shadow-slate-900/10 border border-[#E2E8F0] px-6 py-4"
              data-testid="hero-stat-card"
            >
              <p className="font-display font-black text-3xl text-[#16A34A] tracking-tight">1 200+</p>
              <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Sikeres kiürítés</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
